import { useState } from 'react';
import { sitePaymentCopy } from '@evi/shared';
import { api, getDisplayErrorMessage } from '@/lib/api';
import { TermsGate } from '@/components/TermsGate';
import { useBankReadiness, type BankReadiness } from '@/components/site/BankSetupBanner';
import styles from './SitePages.module.css';
import { CanadianPayoutNote } from '@/components/payouts/CanadianPayoutNote';
import { SiteHostStatement } from '@/components/ui/SiteHostStatement';
import { payoutLocale } from '@/lib/sitePayouts';
import { payoutSetupCopy, splitLinkTemplate } from '@/lib/payoutSetupCopy';

/** statement_locations is every location whose statement this person may read; an older Api sends only locations. */
type Readiness = BankReadiness & { statement_locations?: Array<{ id: string; name: string }> };

export function OwnerPayoutsPage() {
  const copy = sitePaymentCopy(payoutLocale());
  const text = payoutSetupCopy(payoutLocale());
  const [warningBefore, warningAfter] = splitLinkTemplate(text.ownerCountryWarning);
  const query = useBankReadiness();
  const data: Readiness | undefined = query.data;
  const statements = data?.statement_locations ?? data?.locations ?? [];
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [country, setCountry] = useState<'US' | 'CA' | ''>('');
  async function open(key: string, personal = false) {
    setBusy(key); setError('');
    try {
      const result = await api.post(personal ? 'me/connect/start' : 'me/owner-bank/start', {
        json: personal ? { surface: 'admin', ...(country ? { country } : {}) } : { key },
      }).json<{ data: { url: string } }>();
      window.location.assign(result.data.url);
    } catch (err) { setError(await getDisplayErrorMessage(err, text.ownerOpenFailed)); setBusy(''); }
  }
  return <div className={styles.page}><TermsGate docs={['payout_terms']} /><h1 className={styles.title}>{copy.pageTitle}</h1>
    <p>{copy.pageIntro}</p>
    <CanadianPayoutNote />
    {statements.map(site => <section key={site.id}><h2>{site.name}</h2><SiteHostStatement locationId={site.id} /></section>)}
    {query.isLoading && <p>{text.ownerChecking}</p>}
    {query.isError && <p role="alert">{text.ownerUnavailable} <button onClick={() => void query.refetch()}>{copy.retry}</button></p>}
    {query.data?.owners.map(o => <section key={o.key} className={styles.card}><h2>{o.label}</h2>
      <p>{o.ready ? text.ownerReady : o.status === 'unavailable' ? text.ownerUnverified : text.ownerAttention}</p>
      {o.setup_kind === 'personal' && <p>{text.ownerPersonalNote}</p>}
      {o.setup_kind === 'personal' && !o.has_account && <label className={styles.field}>{text.bankAccountCountry}
        <select className={styles.select} value={country} onChange={e => setCountry(e.target.value as 'US' | 'CA' | '')}>
          <option value="">{text.chooseCountry}</option><option value="US">{text.countryUS}</option><option value="CA">{text.countryCA}</option>
        </select>
      </label>}
      {/* LocationBankCard's cannot-change warning: the Api creates no account
          without a country, and Stripe cannot change it afterwards. */}
      {o.setup_kind === 'personal' && !o.has_account && <p className={styles.muted}>{warningBefore}<a href="mailto:support@evinitiative.com">{text.contactSupportAt}</a>{warningAfter}</p>}
      {o.has_account || o.setup_kind === 'personal' ? <button className={styles.primary}
        disabled={!!busy || (o.setup_kind === 'personal' && !o.has_account && !country)} onClick={() => void open(o.key, o.setup_kind === 'personal')}>
        {busy === o.key ? text.openingStripe : text.ownerContinueInStripe}</button>
        : o.earn_url && <a className={styles.primary} href={o.earn_url}>{text.ownerEarnSetup}</a>}
    </section>)}
    {data && data.owners.length === 0 && data.locations.length === 0 && statements.length === 0 && <p>{text.ownerEmpty}</p>}
    {error && <p role="alert" className={styles.error}>{error}</p>}
  </div>;
}
